import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal, Signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { buildInfo } from '../../../buildinfo';
import { environment } from '../../../environments/environment';
import { ApiService, EnvironmentInfoResponse } from '../../core/services/api.service';
import { DatabaseService } from '../../core/services/database.service';
import { ErudaService } from '../../core/services/eruda.service';
import { LoggerService } from '../../core/services/logger.service';
import { NetworkStateService } from '../../core/services/network-state.service';
import { PwaInstallService } from '../../core/services/pwa-install.service';
import { ReactiveHttpService } from '../../core/services/reactive-http.service';
import { SyncInitService } from '../../core/services/sync-init.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { isNativeShell, nativePlatform } from '../../core/utils/native-shell';
import { reloadPage } from '../../core/utils/reload';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { ChipComponent } from '../../shared/components/chip/chip.component';
import { CheckboxComponent } from '../../shared/components/form-controls/checkbox/checkbox.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { TippyDirective } from '../../shared/directives/tippy.directive';
import { DateTimezonePipe } from '../../shared/pipes/date-timezone.pipe';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

@Component({
  selector: 'app-diagnostics',
  imports: [
    CommonModule,
    FormsModule,
    TippyDirective,
    CardComponent,
    CardContentComponent,
    ChipComponent,
    IconComponent,
    ButtonComponent,
    CheckboxComponent,
    DateTimezonePipe,
    TranslatePipe,
  ],
  templateUrl: './diagnostics.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './diagnostics.component.css',
})
export class DiagnosticsComponent implements OnInit {
  private apiService = inject(ApiService);
  private toast = inject(ToastService);
  syncService = inject(SyncInitService);
  private router = inject(Router);
  private reactiveHttp = inject(ReactiveHttpService);
  networkState = inject(NetworkStateService);
  private db = inject(DatabaseService);

  apiStatus = signal<string | null>(null);
  autoDismiss = signal(true);
  isNuclearResetting = signal(false);

  private translate = inject(TranslationService);
  appName = this.translate.translate('app.name');
  environment = environment;
  buildInfo = buildInfo;
  private loggerService = inject(LoggerService);
  private logger = this.loggerService.getLogger('diagnostics');

  // Stream logs to backend
  streamLogsEnabled = signal(this.loggerService.isStreamLogsEnabled());
  streamLogsExpiresAt = signal<Date | null>(this.loggerService.getStreamLogsExpiresAt());
  public erudaService = inject(ErudaService);
  public pwaInstall = inject(PwaInstallService);

  // Runtime shell: native Capacitor app vs. browser
  isNativeApp = isNativeShell();
  nativePlatformName = nativePlatform();
  /** Installed native build, e.g. "1.1 (4)"; null on web or if unavailable. */
  nativeBuild = signal<string | null>(null);
  /** OTA live-update bundle id in use; null = the bundle built into the native app. */
  liveUpdateBundleId = signal<string | null>(null);

  private async loadNativeInfo(): Promise<void> {
    try {
      const [{ App }, { LiveUpdate }] = await Promise.all([
        import('@capacitor/app'),
        import('@capawesome/capacitor-live-update'),
      ]);
      const [info, bundle] = await Promise.all([App.getInfo(), LiveUpdate.getCurrentBundle()]);
      this.nativeBuild.set(`${info.version} (${info.build})`);
      this.liveUpdateBundleId.set(bundle.bundleId);
    } catch (error) {
      this.logger.warn('Failed to read native app info:', error);
    }
  }

  triggerInstallPrompt(): void {
    this.pwaInstall.resetPromptFlag();
    this.pwaInstall.showInstallPrompt();
  }

  // Backend environment info signals
  backendEnvironmentInfo!: Signal<EnvironmentInfoResponse | null>;
  backendEnvironmentLoading!: Signal<boolean>;
  backendEnvironmentError!: Signal<string | null>;
  private refreshBackendInfoFn!: () => void;

  ngOnInit() {
    this.testApi();
    if (this.isNativeApp) void this.loadNativeInfo();

    // Create reactive query for backend environment info
    const backendInfoQuery = this.reactiveHttp.createQuery(
      () => this.apiService.getEnvironmentInfo(),
      {
        respectOfflineMode: true,
        errorMessage: 'Failed to load backend environment information',
        loggerContext: 'diagnostics-backend-info',
      },
    );

    this.backendEnvironmentInfo = backendInfoQuery.data;
    this.backendEnvironmentLoading = backendInfoQuery.loading;
    this.backendEnvironmentError = backendInfoQuery.error;
    this.refreshBackendInfoFn = backendInfoQuery.refresh;
  }

  testApi() {
    this.apiStatus.set('testing...');
    this.apiService.getHealth().subscribe({
      next: (response) => {
        this.apiStatus.set('connected');
      },
      error: (error) => {
        this.apiStatus.set('disconnected');
      },
    });
  }

  testSuccessToast() {
    this.logger.debug('testSuccessToast called, autoDismiss:', this.autoDismiss());
    this.toast.success('Success! This is a success notification.', this.autoDismiss());
  }

  testErrorToast() {
    this.logger.debug('testErrorToast called, autoDismiss:', this.autoDismiss());
    this.toast.error('Error! This is an error notification.', this.autoDismiss());
  }

  testWarningToast() {
    this.logger.debug('testWarningToast called, autoDismiss:', this.autoDismiss());
    this.toast.warning('Warning! This is a warning notification.', this.autoDismiss());
  }

  testInfoToast() {
    this.logger.debug('testInfoToast called, autoDismiss:', this.autoDismiss());
    this.toast.info('Info! This is an info notification.', this.autoDismiss());
  }

  viewLogging() {
    this.router.navigate(['/settings/logging']);
  }

  viewNetworkActivity() {
    this.router.navigate(['/settings/network-activity']);
  }

  async copyErudaLogs() {
    const success = await this.erudaService.copyLogsToClipboard();
    if (success) {
      this.toast.success('Logs copied to clipboard');
    } else {
      this.toast.error('Failed to copy logs');
    }
  }
  async toggleErudaEnabled(enabled: boolean) {
    if (enabled) {
      await this.erudaService.enable();
      this.toast.success('Eruda debugger enabled');
    } else {
      this.erudaService.disable();
      this.toast.success('Eruda debugger disabled');
    }
  }
  toggleStreamLogs(enabled: boolean) {
    this.loggerService.setStreamLogsEnabled(enabled);
    this.streamLogsEnabled.set(this.loggerService.isStreamLogsEnabled());
    this.streamLogsExpiresAt.set(this.loggerService.getStreamLogsExpiresAt());
    if (enabled) {
      this.toast.success('Streaming logs to backend (auto-off in 1 hour)');
    } else {
      this.toast.info('Log streaming disabled');
    }
  }

  async nuclearReset() {
    if (
      !confirm('⚠️ WARNING: This will delete ALL local databases and reload the app. Are you sure?')
    ) {
      return;
    }

    this.isNuclearResetting.set(true);
    try {
      await this.db.nuclearReset();
      this.toast.success('All databases deleted. Reloading...');
      setTimeout(() => reloadPage('diagnostics: database reset complete'), 1000);
    } catch (error) {
      this.logger.error('Nuclear reset failed:', error);
      this.toast.error('Failed to delete all databases');
      this.isNuclearResetting.set(false);
    }
  }

  refreshBackendEnvironmentInfo() {
    this.refreshBackendInfoFn();
  }
}
